<template>
  <div class="energy-analysis-page">
    <el-row :gutter="16" class="chart-row chart-row-top">
      <el-col :span="12">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="card-header">今日 / 昨日能耗对比</div>
          <div class="compare-chart">
            <div v-for="item in compareRows" :key="item.key" class="compare-row">
              <div class="label">{{ item.name }}</div>
              <div class="bar-line">
                <div class="bar bar-today" :style="{ width: item.todayPercent + '%' }"></div>
                <span class="bar-value">{{ item.today }}</span>
              </div>
              <div class="bar-line">
                <div class="bar bar-yesterday" :style="{ width: item.yesterdayPercent + '%' }"></div>
                <span class="bar-value">{{ item.yesterday }}</span>
              </div>
            </div>
          </div>
          <div class="legend-row">
            <div class="legend-item">
              <span class="dot dot-today"></span>
              <span>今日</span>
            </div>
            <div class="legend-item">
              <span class="dot dot-yesterday"></span>
              <span>昨日</span>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="12">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="card-header">本月累计占比</div>
          <div class="pie-wrap">
            <div class="pie" :style="{ background: pieGradient }"></div>
            <div class="pie-legend">
              <div v-for="item in pieRows" :key="item.key" class="pie-legend-item">
                <span class="dot" :style="{ backgroundColor: item.color }"></span>
                <span>{{ item.name }} {{ item.percent }}%</span>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row chart-row-bottom">
      <el-col :span="24">
        <el-card shadow="never" class="chart-card line-card">
          <div slot="header" class="card-header">本月波动趋势</div>
          <div class="line-chart">
            <svg viewBox="0 0 700 320" class="svg-box" xmlns="http://www.w3.org/2000/svg">
              <g>
                <line
                  v-for="n in 5"
                  :key="'grid-y-' + n"
                  x1="70"
                  :y1="20 + n * 50"
                  x2="680"
                  :y2="20 + n * 50"
                  stroke="#e9eff5"
                  stroke-width="2"
                />
              </g>
              <polyline
                v-for="series in lineSeries"
                :key="series.key"
                :points="series.points"
                fill="none"
                :stroke="series.color"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="legend-row">
            <div v-for="series in lineSeries" :key="series.key" class="legend-item">
              <span class="dot" :style="{ backgroundColor: series.color }"></span>
              <span>{{ series.name }}</span>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
const ENERGY_ITEMS = [
  { key: 'light', name: '公共照明', color: '#f7b500', base: 760 },
  { key: 'elevator', name: '电梯运行', color: '#21b2de', base: 280 },
  { key: 'ac', name: '中央空调', color: '#f553a6', base: 410 },
  { key: 'pump', name: '水泵', color: '#74c947', base: 190 }
]

function randIn(min, max) {
  return Math.round(min + Math.random() * (max - min))
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

export default {
  name: 'EnergyAnalysis',
  data() {
    return {
      compareRows: [],
      pieRows: [],
      pieGradient: '',
      lineSeries: []
    }
  },
  created() {
    this.buildCharts()
  },
  methods: {
    buildCharts() {
      const compareSource = ENERGY_ITEMS.map((item) => {
        const today = randIn(item.base - 55, item.base + 55)
        const yesterday = clamp(today + randIn(-45, 45), item.base - 70, item.base + 70)
        return {
          ...item,
          today,
          yesterday
        }
      })

      const maxCompare = Math.max(...compareSource.map(i => Math.max(i.today, i.yesterday)))
      this.compareRows = compareSource.map(i => ({
        key: i.key,
        name: i.name,
        today: i.today.toFixed(1),
        yesterday: i.yesterday.toFixed(1),
        todayPercent: ((i.today / maxCompare) * 100).toFixed(2),
        yesterdayPercent: ((i.yesterday / maxCompare) * 100).toFixed(2)
      }))

      const pieTotal = compareSource.reduce((sum, item) => sum + item.today * 30, 0)
      let acc = 0
      const pieRows = compareSource.map((item, idx) => {
        const raw = (item.today * 30) / pieTotal
        let percent = +(raw * 100).toFixed(2)
        if (idx === compareSource.length - 1) {
          percent = +(100 - acc).toFixed(2)
        }
        acc += percent
        return {
          key: item.key,
          name: item.name,
          color: item.color,
          percent
        }
      })
      this.pieRows = pieRows
      this.pieGradient = this.makePieGradient(pieRows)

      this.lineSeries = ENERGY_ITEMS.map((item) => {
        const values = this.buildSmoothSeries(item.base, 30)
        return {
          key: item.key,
          name: item.name,
          color: item.color,
          values,
          points: this.toLinePoints(values)
        }
      })
    },
    buildSmoothSeries(base, days) {
      const list = []
      let current = randIn(base - 30, base + 30)
      for (let i = 0; i < days; i++) {
        current = clamp(current + randIn(-18, 18), base - 70, base + 70)
        list.push(current)
      }
      return list
    },
    toLinePoints(values) {
      const minVal = Math.min(...values) - 20
      const maxVal = Math.max(...values) + 20
      const width = 610
      const left = 70
      const top = 20
      const height = 250
      return values
        .map((value, idx) => {
          const x = left + (idx / (values.length - 1)) * width
          const y = top + ((maxVal - value) / (maxVal - minVal)) * height
          return `${x.toFixed(2)},${y.toFixed(2)}`
        })
        .join(' ')
    },
    makePieGradient(rows) {
      let start = 0
      const seg = rows
        .map((row) => {
          const end = +(start + row.percent).toFixed(2)
          const str = `${row.color} ${start}% ${end}%`
          start = end
          return str
        })
        .join(', ')
      return `conic-gradient(${seg})`
    }
  }
}
</script>

<style scoped lang="scss">
.energy-analysis-page {
  padding: 16px;
  min-height: 100%;
  box-sizing: border-box;
}

.chart-row {
  margin-bottom: 16px;
}

.chart-row-bottom {
  margin-bottom: 0;
}

.chart-card {
  border: 1px solid #e6f0ff;
  background: #f8fbff;
  height: 100%;
}

.chart-card ::v-deep .el-card__header {
  padding: 14px 20px;
  border-bottom: 1px solid #e6f0ff;
  background: #fff;
}

.chart-card ::v-deep .el-card__body {
  padding: 20px;
}

.card-header {
  font-size: 15px;
  font-weight: 600;
  color: #2d3f56;
}

.chart-row-top .chart-card ::v-deep .el-card__body {
  min-height: 320px;
  box-sizing: border-box;
}

.compare-row {
  margin-bottom: 18px;
}

.compare-row:last-child {
  margin-bottom: 0;
}

.label {
  font-size: 14px;
  color: #4d5e74;
  margin-bottom: 8px;
}

.bar-line {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.bar {
  height: 14px;
  border-radius: 999px;
  min-width: 2px;
  max-width: calc(100% - 80px);
  transition: width 0.3s ease;
}

.bar-today {
  background: #21b2de;
}

.bar-yesterday {
  background: #b6c83f;
}

.bar-value {
  font-size: 13px;
  color: #5f738d;
  margin-left: 12px;
  flex-shrink: 0;
}

.legend-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-right: 24px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #607186;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.dot-today {
  background: #21b2de;
}

.dot-yesterday {
  background: #b6c83f;
}

.pie-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
  min-height: 280px;
}

.pie {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pie-legend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #607186;
  margin-bottom: 10px;
}

.line-chart {
  background: #fff;
  border-radius: 8px;
  padding: 8px;
}

.svg-box {
  width: 100%;
  height: 360px;
  display: block;
}
</style>
